import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Search, PackageCheck, Truck, PackageOpen, Archive, AlertTriangle, ChevronRight, Trash2, UserRound, Tags } from 'lucide-react';
import KitDetail from './KitDetail';
import { TemplateSelect, TemplateManager } from './KitTemplates';
import ToolListPrices from './ToolListPrices';
import TruckBoxes from './TruckBoxes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tool-crib`;
const fmtD = (d) => d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
const fmtReq = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
// How loud an unpacked box should be, from its start date (Toronto days)
const urgency = (k) => {
  if (k.status !== 'to_pack' || !k.start_date) return null;
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  const days = Math.round((new Date(`${k.start_date.slice(0, 10)}T12:00:00`) - new Date(`${today}T12:00:00`)) / 86400000);
  if (days < 0) return { cls: 'bg-red-600 text-white animate-pulse', text: `🚨 OVERDUE — started ${-days}d ago` };
  if (days === 0) return { cls: 'bg-red-600 text-white animate-pulse', text: '🚨 CREW LEAVES TODAY' };
  if (days === 1) return { cls: 'bg-red-600 text-white', text: '🚨 CREW LEAVES TOMORROW' };
  return { cls: 'bg-amber-100 text-amber-900', text: `leaves in ${days} days` };
};

const TABS = [
  { id: 'to_pack', label: 'To Pack', icon: PackageCheck, filter: (k) => k.status === 'to_pack' },
  { id: 'out', label: 'Out on Site', icon: Truck, filter: (k) => k.status === 'packed' && !k.job_over },
  { id: 'to_unpack', label: 'To Unpack', icon: PackageOpen, filter: (k) => k.status === 'packed' && k.job_over },
  { id: 'closed', label: 'Closed', icon: Archive, filter: (k) => k.status === 'closed' },
];

export default function ToolCrib() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const [kits, setKits] = useState([]);
  const [canCrib, setCanCrib] = useState(false);
  const [tab, setTab] = useState(params.get('tab') === 'trucks' ? 'trucks' : 'to_pack');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const [newWo, setNewWo] = useState('');
  const [newTpl, setNewTpl] = useState('');
  const [manageTpl, setManageTpl] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [purgeAsk, setPurgeAsk] = useState(false);
  const [purgeLines, setPurgeLines] = useState(true);

  const purgeClosed = async () => {
    try {
      const r = await axios.post(`${API}/kits/purge-closed`, { purge_costing: purgeLines });
      toast.success(`Deleted ${r.data.deleted} closed box${r.data.deleted === 1 ? '' : 'es'}${purgeLines ? ` and ${r.data.costed_lines_removed} test timesheet line${r.data.costed_lines_removed === 1 ? '' : 's'}` : ''}`);
      setPurgeAsk(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/kits`);
      setKits(r.data.kits || []);
      setCanCrib(!!r.data.can_crib);
    } catch { toast.error('Could not load kits'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // deep links: ?kit=<id> or ?wo=<wo> (crew "what's in my truck")
  useEffect(() => {
    const kitId = params.get('kit'); const wo = params.get('wo');
    (async () => {
      try {
        if (kitId) { const r = await axios.get(`${API}/kits/${kitId}`); setOpen(r.data); }
        else if (wo) { const r = await axios.get(`${API}/kits/for-wo/${encodeURIComponent(wo)}`); if (r.data.kits?.[0]) setOpen(r.data.kits[0]); else toast.info(`No kit for WO ${wo} yet`); }
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, [location.search]);

  const openKit = async (k) => {
    try { const r = await axios.get(`${API}/kits/${k.id}`); setOpen(r.data); navigate(`/tool-crib?kit=${k.id}`, { replace: true }); }
    catch { toast.error('Could not open kit'); }
  };
  const closeKit = () => { setOpen(null); navigate('/tool-crib', { replace: true }); load(); };

  const createKit = async () => {
    const wo = newWo.trim();
    if (!wo) return;
    setCreating(true);
    try {
      const r = await axios.post(`${API}/kits`, { work_order_number: wo, ...(newTpl ? { template_id: newTpl } : {}) });
      toast.success(r.data.existing ? `Opened the existing kit for ${wo}` : `Kit ${r.data.kit_number} created for ${wo}${newTpl ? ' from template' : ''}`);
      setNewWo(''); setOpen(r.data); navigate(`/tool-crib?kit=${r.data.id}`, { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create kit'); }
    setCreating(false);
  };

  if (open) {
    return (
      <div className="space-y-4" data-testid="tool-crib">
        <KitDetail kit={open} canCrib={canCrib && open.can_crib !== false} onBack={closeKit} onChanged={(k) => { setOpen(k); load(); }} />
      </div>
    );
  }

  const counts = Object.fromEntries(TABS.map(t => [t.id, kits.filter(t.filter).length]));
  const ql = q.trim().toLowerCase();
  const shown = kits.filter((TABS.find(t => t.id === tab) || TABS[0]).filter)
    .filter(k => !ql || [k.work_order_number, k.customer_name, k.job_description, k.kit_number, k.requested_by_name, ...(k.crew || [])].join(' ').toLowerCase().includes(ql))
    .sort((a, b) => (a.start_date || '').localeCompare(b.start_date || '') || (a.updated_at || '').localeCompare(b.updated_at || ''));

  return (
    <div className="space-y-4" data-testid="tool-crib">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tool Crib</h1>
          <p className="text-sm text-slate-500 mt-0.5">Job kits — build the list, pack it, verify it came back. Anything short is costed to the job.</p>
        </div>
        {canCrib && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="kit-new">
            <button onClick={() => navigate('/material-sheets')} className="px-3 py-2.5 rounded-xl bg-slate-900/5 hover:bg-slate-900/10 text-slate-900 text-sm font-semibold" title="Costed consumable pick lists" data-testid="crib-material-sheets-link">Material Sheets →</button>
            <input value={newWo} onChange={e => setNewWo(e.target.value)} onKeyDown={e => e.key === 'Enter' && createKit()} placeholder="WO # e.g. 26-7051"
              className="px-3 py-2.5 rounded-xl bg-white text-slate-900 text-sm w-40 outline-none focus:ring-2 focus:ring-emerald-400" data-testid="kit-new-wo" />
            <TemplateSelect value={newTpl} onChange={setNewTpl} />
            <button onClick={createKit} disabled={creating || !newWo.trim()} className="px-4 py-2.5 rounded-xl bg-emerald-600 disabled:bg-slate-300 text-white font-bold text-sm flex items-center gap-1.5" data-testid="kit-new-btn"><Plus className="w-4 h-4" /> New kit</button>
            <button onClick={() => setManageTpl(true)} className="px-3 py-2.5 rounded-xl bg-slate-900/5 text-slate-900 text-sm font-semibold" title="Manage kit templates" data-testid="kit-templates-manage">Templates</button>
          </div>
        )}
      </div>
      {manageTpl && <TemplateManager onClose={() => setManageTpl(false)} />}

      <div className="flex gap-1 bg-white p-1 rounded-xl border border-slate-200 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`crib-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap flex-shrink-0 transition-all ${tab === t.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
            <span className={`ml-1 text-[11px] px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-slate-900/10' : 'bg-slate-100 text-slate-700'}`} data-testid={`crib-count-${t.id}`}>{counts[t.id]}</span>
          </button>
        ))}
        <button onClick={() => { setTab('trucks'); navigate('/tool-crib?tab=trucks', { replace: true }); }} data-testid="crib-tab-trucks" title="Standard tool set on each service truck, by foreman"
          className={`${canCrib ? '' : 'ml-auto '}flex items-center gap-1.5 px-3 sm:px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap flex-shrink-0 transition-all ${tab === 'trucks' ? 'bg-sky-600 text-white shadow-lg shadow-sky-600/30' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}>
          <Truck className="w-4 h-4" /> Truck boxes
        </button>
        {canCrib && (
          <button onClick={() => setTab('prices')} data-testid="crib-tab-prices" title="Link tool-list consumables to their price in the consumables module"
            className={`ml-auto flex items-center gap-1.5 px-3 sm:px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap flex-shrink-0 transition-all ${tab === 'prices' ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/30' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}>
            <Tags className="w-4 h-4" /> Tool list prices
          </button>
        )}
      </div>

      {tab === 'prices' && <ToolListPrices />}
      {tab === 'trucks' && <TruckBoxes />}

      {tab !== 'prices' && tab !== 'trucks' && <>
      {canCrib && tab === 'closed' && counts.closed > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-800" data-testid="crib-purge-bar">
          {purgeAsk ? (
            <>
              <label className="flex items-center gap-2 text-xs sm:text-sm"><input type="checkbox" checked={purgeLines} onChange={(e) => setPurgeLines(e.target.checked)} data-testid="crib-purge-lines" /> Also remove their costed timesheet lines (test data)</label>
              <div className="flex gap-2">
                <button onClick={() => setPurgeAsk(false)} className="px-3 py-1.5 rounded-lg bg-slate-900/5 font-semibold" data-testid="crib-purge-cancel">Keep</button>
                <button onClick={purgeClosed} className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 text-white font-bold" data-testid="crib-purge-yes">Delete all {counts.closed} closed</button>
              </div>
            </>
          ) : (
            <>
              <span>Testing clean-up: remove every closed box in one go.</span>
              <button onClick={() => setPurgeAsk(true)} className="px-3 py-1.5 rounded-lg bg-slate-900/5 hover:bg-red-100 font-semibold inline-flex items-center gap-1.5" data-testid="crib-purge-open"><Trash2 className="w-4 h-4" /> Delete all closed boxes</button>
            </>
          )}
        </div>
      )}

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Find a kit — WO, customer, crew…" className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white/95 text-slate-900 text-sm outline-none focus:ring-2 focus:ring-blue-400" data-testid="crib-search" />
      </div>

      {loading && <p className="text-slate-700 text-sm">Loading…</p>}
      {!loading && shown.length === 0 && (
        <div className="bg-slate-900/5 rounded-2xl p-8 text-center text-slate-600" data-testid="crib-empty">
          {tab === 'to_pack' ? 'Nothing waiting to be packed. Approved dispatches with a tool list show up here automatically — or start one with "New kit".' :
           tab === 'out' ? 'No kits out on site.' : tab === 'to_unpack' ? 'No returned kits waiting to be checked in.' : 'No closed kits yet.'}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="crib-kit-list">
        {shown.map(k => (
          <button key={k.id} onClick={() => openKit(k)} className="text-left bg-white rounded-2xl shadow hover:shadow-lg transition-shadow p-4 flex flex-col gap-2 min-h-[120px]" data-testid={`crib-kit-${k.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-mono text-lg font-extrabold text-blue-700 leading-none">{k.work_order_number}</p>
                <p className="font-semibold text-slate-900 truncate mt-1">{k.customer_name || '—'}</p>
                {k.job_description && <p className="text-xs text-slate-500 truncate">{k.job_description}</p>}
              </div>
              <ChevronRight className="w-5 h-5 text-slate-500 flex-shrink-0" />
            </div>
            {k.status === 'to_pack' && k.start_date && (
              <p className="text-base font-extrabold text-slate-900 leading-tight" data-testid={`crib-kit-date-${k.id}`}>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1.5">Leaves</span>
                {new Date(`${k.start_date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </p>
            )}
            <div className="flex items-center gap-2 flex-wrap text-xs text-slate-600 mt-auto">
              <span className="font-mono text-slate-500">{k.kit_number}</span>
              {urgency(k) && <span className={`px-2 py-0.5 rounded-full font-extrabold ${urgency(k).cls}`} data-testid={`crib-urgency-${k.id}`}>{urgency(k).text}</span>}
              {(k.start_date || k.end_date) && k.status !== 'to_pack' && <span>{fmtD(k.start_date)}{k.end_date && k.end_date !== k.start_date ? ` – ${fmtD(k.end_date)}` : ''}</span>}
              <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 font-semibold">{k.tool_count} tools</span>
              {k.consumable_count > 0 && <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 font-semibold">{k.consumable_count} consumables/materials</span>}
              {k.status === 'to_pack' && k.packed_count > 0 && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold" data-testid={`crib-packing-${k.id}`}>packing… {k.packed_count}/{k.item_count}{k.packing_by ? ` · ${k.packing_by.split(' ')[0]}` : ''}</span>}
              {k.status === 'packed' && k.job_over && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold">Job ended — check in</span>}
              {k.status === 'closed' && k.missing_tools > 0 && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-bold inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{k.missing_tools} missing</span>}
              {k.status === 'closed' && k.costed_total > 0 && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-800 font-semibold">${k.costed_total.toFixed(2)} costed</span>}
            </div>
            {k.crew?.length > 0 && <p className="text-xs text-slate-500 truncate">{k.crew.join(', ')}</p>}
            {k.requested_by_name && <p className="text-xs text-amber-700 font-semibold truncate inline-flex items-center gap-1" data-testid={`crib-requested-by-${k.id}`}><UserRound className="w-3 h-3" />Requested by {k.requested_by_name}{k.requested_at ? ` · ${fmtReq(k.requested_at)}` : ''}</p>}
          </button>
        ))}
      </div>
      </>}
    </div>
  );
}
